import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useCookieConsent } from "../../hooks/useCookieConsent";
import { cookieConsentConfig } from "../../services/cookieConsent/cookieConfig";

export const CookieConsentBanner = React.memo(function CookieConsentBanner() {
  const { consent, showBanner, acceptAll, rejectNonEssential, updateConsent } =
    useCookieConsent();
  const isReopened = consent.isResolved && showBanner;
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(false);
  const [functionalChecked, setFunctionalChecked] = useState(false);

  useEffect(() => {
    if (!showBanner) {
      setShowPreferences(false);
      return;
    }

    if (isReopened) {
      setAnalyticsChecked(consent.analytics);
      setFunctionalChecked(consent.functional);
      setShowPreferences(true);
    } else {
      setAnalyticsChecked(false);
      setFunctionalChecked(false);
      setShowPreferences(false);
    }
  }, [showBanner, isReopened, consent.analytics, consent.functional]);
  const bannerRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (!showBanner || !bannerRef.current) return;

    const firstButton = bannerRef.current.querySelector("button");
    firstButton?.focus();
  }, [showBanner, showPreferences]);

  useEffect(() => {
    if (!showBanner) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        rejectNonEssential();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showBanner, rejectNonEssential]);

  const handleSavePreferences = useCallback(() => {
    updateConsent(analyticsChecked, functionalChecked);
  }, [analyticsChecked, functionalChecked, updateConsent]);

  const handleManagePreferences = useCallback(() => {
    setShowPreferences(true);
  }, []);

  const handleBackToBanner = useCallback(() => {
    setShowPreferences(false);
  }, []);

  if (!showBanner) return null;

  return (
    <dialog
      ref={bannerRef}
      open
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[9999] m-0 w-full max-w-none border-t border-[var(--borderColor)] bg-[var(--pageBackground)] p-4 shadow-lg"
    >
      {!showPreferences ? (
        <div className="mx-auto flex max-w-3xl flex-col gap-3">
          <h2 className="text-base font-semibold text-[var(--primaryColor)]">
            Cookie preferences
          </h2>
          <p className="text-sm text-[var(--secondaryColor)]">
            We use cookies to provide essential functionality, analyse usage,
            and offer in-app support. You can accept all cookies, reject
            non-essential cookies, or manage your preferences.{" "}
            <Link
              to={cookieConsentConfig.policyUrl}
              className="underline text-[var(--linkColor,#2563eb)] hover:text-[var(--linkHoverColor,#1d4ed8)]"
            >
              Cookie policy
            </Link>
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={acceptAll}
              className="rounded-md bg-[var(--buttonPrimaryBackground,#2563eb)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--buttonPrimaryBackground,#2563eb)] focus:ring-offset-2"
            >
              Accept all
            </button>
            <button
              type="button"
              onClick={rejectNonEssential}
              className="rounded-md border border-[var(--borderColor)] bg-[var(--pageBackground)] px-4 py-2 text-sm font-medium text-[var(--primaryColor)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--buttonPrimaryBackground,#2563eb)] focus:ring-offset-2"
            >
              Reject non-essential
            </button>
            <button
              type="button"
              onClick={handleManagePreferences}
              className="rounded-md border border-[var(--borderColor)] bg-[var(--pageBackground)] px-4 py-2 text-sm font-medium text-[var(--primaryColor)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--buttonPrimaryBackground,#2563eb)] focus:ring-offset-2"
            >
              Manage preferences
            </button>
          </div>
        </div>
      ) : (
        <div className="mx-auto flex max-w-3xl flex-col gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-[var(--primaryColor)]">
              Manage cookie preferences
            </h2>
            <button
              type="button"
              onClick={handleBackToBanner}
              className="text-sm text-[var(--secondaryColor)] underline hover:text-[var(--primaryColor)] focus:outline-none focus:ring-2 focus:ring-[var(--buttonPrimaryBackground,#2563eb)] focus:ring-offset-2"
            >
              Back
            </button>
          </div>

          <fieldset className="flex flex-col gap-3">
            <legend className="sr-only">Cookie categories</legend>

            <label className="flex items-center gap-3 opacity-60">
              <input
                type="checkbox"
                checked
                disabled
                className="h-4 w-4 rounded"
                aria-label="Essential"
                aria-describedby="essential-desc"
              />
              <span className="flex flex-col">
                <span className="text-sm font-medium text-[var(--primaryColor)]">
                  Essential
                </span>
                <span
                  id="essential-desc"
                  className="text-xs text-[var(--secondaryColor)]"
                >
                  Required for authentication and core functionality. Always
                  active.
                </span>
              </span>
            </label>

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={analyticsChecked}
                onChange={(e) => setAnalyticsChecked(e.target.checked)}
                className="h-4 w-4 rounded"
                aria-label="Analytics"
                aria-describedby="analytics-desc"
              />
              <span className="flex flex-col">
                <span className="text-sm font-medium text-[var(--primaryColor)]">
                  Analytics
                </span>
                <span
                  id="analytics-desc"
                  className="text-xs text-[var(--secondaryColor)]"
                >
                  Application monitoring, session replay, and usage analytics
                  (Coralogix, Microsoft Clarity, Google Analytics).
                </span>
              </span>
            </label>

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={functionalChecked}
                onChange={(e) => setFunctionalChecked(e.target.checked)}
                className="h-4 w-4 rounded"
                aria-label="Functional"
                aria-describedby="functional-desc"
              />
              <span className="flex flex-col">
                <span className="text-sm font-medium text-[var(--primaryColor)]">
                  Functional
                </span>
                <span
                  id="functional-desc"
                  className="text-xs text-[var(--secondaryColor)]"
                >
                  In-app customer support widget (Intercom).
                </span>
              </span>
            </label>
          </fieldset>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSavePreferences}
              className="rounded-md bg-[var(--buttonPrimaryBackground,#2563eb)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--buttonPrimaryBackground,#2563eb)] focus:ring-offset-2"
            >
              Save preferences
            </button>
          </div>
        </div>
      )}
    </dialog>
  );
});
